import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Loader2, TrendingUp, AlertCircle } from "lucide-react";

/**
 * Super-admin scoreboard — one row per pub summarising trading health at
 * a glance: entrants, gross paid, configured voucher count, and vouchers
 * actually redeemed. Sorted by net revenue descending on the backend.
 *
 * Feeds off `GET /api/admin/performance-by-org` which also folds in pubs
 * with zero activity so dormant tenants are visible (not hidden behind a
 * revenue filter).
 */

function formatMoney(pence, currency = "gbp") {
  if (typeof pence !== "number") return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(pence / 100);
  } catch {
    return `${(pence / 100).toFixed(2)}`;
  }
}

function toCsv(rows) {
  const header = [
    "Organisation",
    "Slug",
    "Entrants",
    "Paid (£)",
    "Paid count",
    "Vouchers",
    "Vouchers redeemed",
  ];
  const lines = [header.join(",")];
  rows.forEach((r) => {
    lines.push(
      [
        `"${(r.pub_name || "").replace(/"/g, '""')}"`,
        r.pub_slug || "",
        r.entrants || 0,
        ((r.paid_pence || 0) / 100).toFixed(2),
        r.paid_count || 0,
        r.voucher_count || 0,
        r.vouchers_redeemed || 0,
      ].join(","),
    );
  });
  return lines.join("\n");
}

export default function PerformanceByOrgCard({
  selectedSlug = "",
  onSelectPub,
  onPubsLoaded,
} = {}) {
  const [state, setState] = useState({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const { data } = await api.get("/admin/performance-by-org");
      setState({ kind: "ready", rows: data || [] });
      if (onPubsLoaded) {
        // Emit the sorted slug/name list so the Stripe card's dropdown
        // stays in lock-step with the same tenant order.
        onPubsLoaded(
          (data || []).map((r) => ({ slug: r.pub_slug, name: r.pub_name })),
        );
      }
    } catch (err) {
      setState({ kind: "error", message: formatApiError(err) });
    }
  }, [onPubsLoaded]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = state.kind === "ready" ? state.rows : [];
  const totals = rows.reduce(
    (acc, r) => ({
      entrants: acc.entrants + (r.entrants || 0),
      paid: acc.paid + (r.paid_pence || 0),
      vouchers: acc.vouchers + (r.voucher_count || 0),
      vouchersRedeemed: acc.vouchersRedeemed + (r.vouchers_redeemed || 0),
    }),
    { entrants: 0, paid: 0, vouchers: 0, vouchersRedeemed: 0 },
  );

  const downloadCsv = () => {
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `performance-by-org-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <section
      className="p-5"
      data-testid="performance-by-org-card"
      aria-label="Performance by organisation"
    >
      <header className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm bg-zinc-950 text-white">
            <TrendingUp className="h-4 w-4" />
          </span>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
              Scoreboard
            </p>
            <h3 className="font-heading text-lg font-bold text-zinc-950">
              Performance by organisation
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={downloadCsv}
            disabled={state.kind !== "ready" || rows.length === 0}
            data-testid="performance-download-csv"
            className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-950 disabled:text-zinc-300"
          >
            Download CSV
          </button>
          <button
            type="button"
            onClick={load}
            disabled={state.kind === "loading"}
            data-testid="performance-refresh"
            className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-950 disabled:text-zinc-300"
          >
            Refresh
          </button>
        </div>
      </header>

      {state.kind === "loading" ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 text-zinc-400 animate-spin" />
        </div>
      ) : state.kind === "error" ? (
        <div className="rounded-sm border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-none" />
          {state.message}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-sm border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">
          No organisations yet.
        </div>
      ) : (
        <div className="overflow-x-auto border border-zinc-200 rounded-sm">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                <th className="text-left px-3 py-2.5">Organisation</th>
                <th className="text-right px-3 py-2.5">Entrants</th>
                <th className="text-right px-3 py-2.5">Paid</th>
                <th className="text-right px-3 py-2.5">Vouchers</th>
                <th className="text-right px-3 py-2.5">Vouchers redeemed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((r) => {
                const isDormant =
                  (r.entrants || 0) === 0 && (r.paid_pence || 0) === 0;
                const isSelected = selectedSlug === r.pub_slug;
                const clickable = Boolean(onSelectPub);
                return (
                  <tr
                    key={r.pub_slug}
                    data-testid={`perf-row-${r.pub_slug}`}
                    onClick={
                      clickable ? () => onSelectPub(r.pub_slug) : undefined
                    }
                    className={[
                      isDormant ? "text-zinc-400" : "text-zinc-950",
                      clickable
                        ? "cursor-pointer hover:bg-zinc-50 transition-colors"
                        : "",
                      isSelected ? "bg-blue-50/60 hover:bg-blue-50" : "",
                    ].join(" ")}
                    title={
                      clickable
                        ? "Click to view this pub's Stripe transactions"
                        : undefined
                    }
                  >
                    <td className="px-3 py-3">
                      <p
                        className="font-heading font-bold leading-tight truncate max-w-[220px]"
                        title={r.pub_name}
                      >
                        {r.pub_name}
                      </p>
                      <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono">
                        {r.pub_slug}
                      </p>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold">
                      {r.entrants || 0}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatMoney(r.paid_pence)}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {r.voucher_count || 0}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {r.vouchers_redeemed || 0}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {rows.length > 1 ? (
              <tfoot className="bg-zinc-50 text-[11px] font-bold uppercase tracking-widest">
                <tr>
                  <td className="px-3 py-2.5 text-zinc-500">
                    Totals · {rows.length} orgs
                  </td>
                  <td
                    className="px-3 py-2.5 text-right tabular-nums text-zinc-950"
                    data-testid="perf-totals-entrants"
                  >
                    {totals.entrants}
                  </td>
                  <td
                    className="px-3 py-2.5 text-right tabular-nums text-zinc-950"
                    data-testid="perf-totals-paid"
                  >
                    {formatMoney(totals.paid)}
                  </td>
                  <td
                    className="px-3 py-2.5 text-right tabular-nums text-zinc-950"
                    data-testid="perf-totals-vouchers"
                  >
                    {totals.vouchers}
                  </td>
                  <td
                    className="px-3 py-2.5 text-right tabular-nums text-zinc-950"
                    data-testid="perf-totals-vouchers-redeemed"
                  >
                    {totals.vouchersRedeemed}
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}
    </section>
  );
}
